import { ChangeEvent, FC, useCallback, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DialogTitleClosable } from "../../../../../common/dialog-utils/DialogTitleClosable";
import { useAppDispatch, useAppSelector } from "../../../../../../store/hooks";
import { getResourceExplorerViewsHierarchyThunk } from "../../../../../../store/resource-explorer/thunks/resource-explorer-hierarchy/getResourceExplorerViewsHierarchyThunk";
import { updateResourceExplorerFolderNameThunk } from "../../../../../../store/resource-explorer/thunks/resource-explorer-hierarchy/updateResourceExplorerFolderNameThunk";
import { updateResourceExplorerFolderNameLoadingSelector } from "../../../../../../store/resource-explorer/selectors/loading/updateResourceExplorerFolderNameLoadingSelector";
import { navigationHierarchyItemNoDndKey } from "../../../sortable-tree/utils/constants";

interface ResourceExplorerViewWithFoldersRenameDialogProps {
  open: boolean;
  id: string;
  name: string;
  closeMenu(): void;
}

export const ResourceExplorerV2ViewFoldersRenameDialog: FC<
  ResourceExplorerViewWithFoldersRenameDialogProps
> = ({ open, closeMenu, id, name }) => {
  const dispatch = useAppDispatch();
  const [value, setValue] = useState(name);

  const loading = useAppSelector(
    updateResourceExplorerFolderNameLoadingSelector,
  );

  const renameHandler = useCallback(async () => {
    await dispatch(
      updateResourceExplorerFolderNameThunk({
        id,
        name: value,
      }),
    );
    dispatch(getResourceExplorerViewsHierarchyThunk());
    closeMenu();
  }, [dispatch, closeMenu, id, value]);

  const inputChangeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value);
    },
    [],
  );

  return (
    <Dialog
      open={open}
      fullWidth
      onClose={closeMenu}
      {...navigationHierarchyItemNoDndKey}
    >
      <DialogTitleClosable title="Rename Folder" onClose={closeMenu} />

      <DialogContent dividers sx={{ py: 3 }}>
        <TextField
          autoFocus
          fullWidth
          type="text"
          value={value}
          size="small"
          variant="outlined"
          onChange={inputChangeHandler}
          label="Folder Name"
          placeholder="Folder Name"
        />
      </DialogContent>

      <DialogActions sx={{ py: 2, px: 3 }}>
        <Button color="tertiary" onClick={closeMenu}>
          Cancel
        </Button>
        <LoadingButton
          disabled={value === name || !value}
          onClick={renameHandler}
          loading={loading}
          variant="contained"
        >
          Rename
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
